/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {formatAddress} from '@ion/dls/src/utils/ComponentUtils';
import Tooltip from '../../components/tooltip/Tooltip';
import {dlsAlt, dlsColor} from '../../theme/theme';

export interface AddressType {
  isCustomVariant?: boolean;
  isAltVariant?: boolean;
  street: string;
  city: string;
  state: string;
  zipcode?: string;
  textAlign?: string;
  paddingLeft?: string;
  fontWeight?: number;
  isPopupVariant?: boolean;
  icon?: JSX.Element;
  toolTipTitle?: string;
}

const useStyles = makeStyles(({typography: {fontFamily}, textAlign, paddingLeft, fontWeight}) => ({
  addressText : props => ({
    color       : dlsColor('grey3'),
    margin      : 0,
    textAlign   : textAlign || 'right',
    fontFamily  : fontFamily,
    padding     : '4px',
    paddingLeft : paddingLeft || '4px',
    fontWeight  : fontWeight || 400,
    ...props
  }),
  altAddressTextTypography : {
    ...dlsAlt('typography', 'label2'),
    margin      : '0',
    textAlign   : textAlign || 'right',
    padding     : '4px',
    paddingLeft : paddingLeft || '4px'
  },
  altAddressStyles : {
    fontWeight : 400,
    fontSize   : '14px',
    lineHeight : '24px',
    padding    : '0 !important',
    color      : `${dlsColor('grey2')} !important`,
    display    : 'inline'
  },
  popupAddressStyles : {
    fontWeight    : '500 !important' as any,
    fontSize      : '10px !important',
    lineHeight    : '14px !important',
    padding       : '0 !important',
    textAlign     : 'left !important' as 'left',
    color         : `${dlsColor('grey3')} !important`,
    letterSpacing : '1px !important',
    whiteSpace    : 'nowrap',
    overflow      : 'hidden',
    textOverflow  : 'ellipsis'
  },
  container : {
    textAlign : 'end'
  },
  iconContainer : {
    display       : 'inline',
    marginRight   : 2,
    verticalAlign : 'sub'
  }
}));

/**
 * Used to display an address that's shown in multiple places per the DLS,
 * most often used in asset cards and popups
 *
 * ```typescript
 *   <Address street='123 Main St'
 *     city='Anytown'
 *     state='CA'
 *     zipcode='12345'
 *     display='flex'/>
 *
 * ```
 */
function Address({
  street,
  city,
  state,
  isCustomVariant,
  isAltVariant,
  isPopupVariant,
  zipcode = '',
  toolTipTitle = '',
  icon = null,
  ...props
}: AddressType) {
  const classes = useStyles(props);

  return (
    <div className={classes.container}>
      {icon && (
        <Tooltip title={toolTipTitle}
          placement={'top'}
          arrow
          disableTouchListener >
          <span className={classes.iconContainer}>
            {icon}
          </span>
        </Tooltip>
      )}
      <p className={`
        ${isCustomVariant ? classes.altAddressTextTypography : classes.addressText}
        ${isAltVariant ? classes.altAddressStyles : null}
        ${isPopupVariant ? classes.popupAddressStyles : null}
      `}>
        {formatAddress({street, city, state, zipcode, isCustomVariant})}
      </p>
    </div>
  );
}

export default Address;
